import React from 'react';

import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';

import { Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import CharterFlightsPricingPage from '~/components/Flights/Charter/CharterFlightsPricingPage';
import lazyImport from '~/components/utils/lazyImport';

import useQuery from '~/hooks/useQuery';

import AtolReports from './AtolReports';
import AtolReportsV2 from './AtolReportsV2';
import CharterInventoryPage from './Charter/CharterInventoryPage';
import CharterLoyaltyPointsPage from './Charter/CharterLoyaltyPointsPage';
import CharterSeatsPage from './Charter/CharterSeatsPage';
import CreditReservationList from './CreditReservationListContainer';
import FDAPage from './FDA/FDAPage';
import FlightCacheToolPage from './FlightCacheTool';
import MerchandisingFlightDeals from './FlightDeals/FlightDeals';
import { FlightForceBundles } from './FlightForceBundles';
import { FlightsCampaings } from './FlightsCampaigns/FlightsCampaigns';
import PendingReservations from './PendingReservations';

const tabsMap = {
  campaigns: {
    label: 'Campaigns',
    component: FlightsCampaings,
  },
  deals: {
    label: 'Deals',
    component: MerchandisingFlightDeals,
  },
  dashboard: {
    label: 'Dashboard',
    component: lazyImport(() => import('./FlightDashboard')),
  },
  pending: {
    label: 'Pending',
    component: PendingReservations,
  },
  credit: {
    label: 'Credit Reservation',
    component: CreditReservationList,
  },
  'atol-reports': {
    label: 'ATOL Reports by Month (Older Feature)',
    component: AtolReports,
  },
  'atol-reports-new': {
    label: 'ATOL Reports by Month (New)',
    component: AtolReportsV2,
  },
  'force-bundles': {
    label: 'Force Bundles',
    component: FlightForceBundles,
  },
  fda: {
    label: 'Flights Configuration',
    component: FDAPage,
  },
  cacheTool: {
    label: 'Caching Tool',
    component: FlightCacheToolPage,
  },
  charterSeats: {
    label: 'Charter Seats',
    component: CharterSeatsPage,
  },
  charterInventory: {
    label: 'Charter Inventory',
    component: CharterInventoryPage,
  },
  charterLoyaltyPoints: {
    label: 'Charter Loyalty Points',
    component: CharterLoyaltyPointsPage,
  },
  charterPrices: {
    label: 'Charter Prices',
    component: CharterFlightsPricingPage,
  },
};
const tabsList = Object.keys(tabsMap);

preRegisterSpotlightRoutes(
  Object.entries(tabsMap).map(([key, tab]) => ({
    path: `/flights?tab=${key}`,
    label: `Flights / ${tab.label}`,
    section: 'Flights',
  })),
);

export function FlightsPageContainer() {
  const query = useQuery();
  const history = useHistory();
  const selectedTab = query.get('tab') || 'campaigns';

  const currentTab = tabsMap[selectedTab];
  const title = `Flights / ${currentTab.label}`;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={selectedTab}
          onChange={(_, selectedTabKey) => history.push(`/flights?tab=${selectedTabKey}`)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabsList.map((tabKey) => (
            <Tab key={tabKey} value={tabKey} label={tabsMap[tabKey].label} />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}

export default FlightsPageContainer;
